import React, { useRef, useState, useEffect, useMemo, useCallback } from "react";
import { debounce } from 'lodash';
import { IconButton, Badge, Tooltip } from "@mui/material";
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { ROUTES } from "../../routes/User/paths";
import DraggableToolbox from './DraggableToolbox';
import useApp from "../../store/contexts/AppContext";
import capitalizeEachFirstWord from "../../utils/CapLetters";
import FormatNumberCounter from "../../utils/FormatNumberCounter";
import SOCKET_EVENTS from "../../config/SocketEvents";

const TEAM_ACCENT_COLORS = {
  admin: "#595c8b",
  team1: "#add8e6",
  team2: "#98fb98",
  team3: "#ffffe0",
  team4: "#e6e6fa",
  team5: "#ffa07a",
  team6: "#d3d3d3",
  team7: "#ffb6c1",
  "team-spectator": "#ffb6c1",
};

const getTeamAccentColor = (teamCode) => TEAM_ACCENT_COLORS[teamCode] || "#ffffff";

const debounceRestore = debounce((callback) => callback(), 100);

const TopVideoWebCam = React.memo(({
  classes,
  classes2,
  isVideoOn,
  isAudioOn,
  myVideoRef,
  handleRoomParticipantsVisibility,
  handleVideoToggle,
  handleAudioToggle,
  showRoomParticipants,
  handleWebcamMenuClick,
  anchorWebcamMenuEl,
  handleWebcamMenuClose,
  openUserWebcamSettingsModal,
  handleRoomTitleToggle,
  handleRoomTeamsVisibility,
  showRoomTeams,
  showRoomTitle,
  userTeamCode,
  userTeamName,
  userTeamScore,
  roomId,
  isUserOnSpectatorMode,
  loadANewPage,
  handleReactionIconExpandClick,
  reactionIconExpanded,
  handleReactionIcon,
  displayVideo,
  handleSendToolboxScreenshot,
  activeTeamParticipantVideos,
  currentToolboxScreenshot,
  countdown,
  socket
}) => {
  const togglingRoomPath = isUserOnSpectatorMode
    ? `${ROUTES.rooms}/${roomId}`
    : `${ROUTES.spectate}/${roomId}`;

  const {
    appState: { userData, roomToolbox },
  } = useApp();

  // Basic state
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [toolboxMode, setToolboxMode] = useState('view');
  const [lineColor, setLineColor] = useState('black');
  const [lineThickness, setLineThickness] = useState(5);
  const [lineOpacity, setLineOpacity] = useState(100);
  const drawingRef = useRef(false);
  const positionRef = useRef({ x: 0, y: 0 });
  const [hasOverlayScreenshot, setHasOverlayScreenshot] = useState(false);

  // New reaction state
  const [reactionCounts, setReactionCounts] = useState({ like: 0, dislike: 0, tip: 0 });
  const [myReactions, setMyReactions] = useState({ like: false, dislike: false, tip: false });
  const pendingReactionsRef = useRef({ like: false, dislike: false, tip: false });

  // Canvas states
  const [isDraggingTextCanvas, setIsDraggingTextCanvas] = useState(false);
  const [TextCanvas, setTextCanvas] = useState([]);
  const [selectedTextCanvas, setSelectedTextCanvas] = useState(-1);
  const [startTextCanvasX, setStartTextCanvasX] = useState(0);
  const [startTextCanvasY, setStartTextCanvasY] = useState(0);

  // Image canvas states
  const [imageCanvas, setImageCanvas] = useState([]);
  const [selectedImageCanvasIndex, setSelectedImageCanvasIndex] = useState(-1);
  const [isDraggingImageCanvas, setIsDraggingImageCanvas] = useState(false);
  const [isResizingImageCanvas, setIsResizingImageCanvas] = useState(false);
  const [resizeHandleImageCanvas, setResizeHandleImageCanvas] = useState(null);

  const [voteCounts, setVoteCounts] = useState({ up: 0, down: 0 });
  const [spectatorVotes, setSpectatorVotes] = useState([]);
  const [hasVoted, setHasVoted] = useState(null);

  // Canvas state object
  const [canvasStates, setCanvasStates] = useState({
    draw: null,
    text: null,
    image: null,
    erase: null,
    teamDraw: null,
    teamText: null,
    teamImage: null,
    teamErase: null
  });

  // Text properties state
  const [textProps, setTextProps] = useState({
    text: '',
    color: '#000000',
    fontSize: 16,
    fontFamily: 'Arial',
    fontStyle: 'normal',
    angle: 0,
    x: 100,
    y: 100,
  });

  // Team-related states
  const [selectedTeamTextCanvas, setSelectedTeamTextCanvas] = useState(-1);
  const [TeamTextCanvas, setTeamTextCanvas] = useState([]);
  const [teamImageCanvas, setTeamImageCanvas] = useState([]);
  const [selectedTeamImageCanvasIndex, setSelectedTeamImageCanvasIndex] = useState(-1);
  const [startTeamTextCanvasX, setStartTeamTextCanvasX] = useState(0);
  const [startTeamTextCanvasY, setStartTeamTextCanvasY] = useState(0);
  const [isDraggingTeamTextCanvas, setIsDraggingTeamTextCanvas] = useState(false);

  // Team text properties
  const [teamTextProps, setTeamTextProps] = useState({
    text: '',
    color: '#000000',
    fontSize: 16,
    fontFamily: 'Arial',
    fontStyle: 'normal',
    angle: 0,
    x: 100,
    y: 100,
  });

  // All refs
  const imageCanvasRef = useRef(null);
  const drawCanvasRef = useRef(null);
  const teamOverlayCanvasRef = useRef(null);
  const canvasVideoRef = useRef(null);
  const eraseCanvasRef = useRef(null);
  const textCanvasRef = useRef(null);
  const compositeCanvasRef = useRef(null);
  const colorPickerRef = useRef(null);
  const opacitySliderRef = useRef(null);
  const toolboxRef = useRef(null);
  const teamCanvasVideoRef = useRef(null);
  const teamDrawCanvasRef = useRef(null);
  const teamEraseCanvasRef = useRef(null);
  const teamTextCanvasRef = useRef(null);
  const teamCompositeCanvasRef = useRef(null);
  const teamImageCanvasRef = useRef(null);

  // Computed values with useMemo
  const showOverlay = useMemo(() => {
    if (!currentToolboxScreenshot) return false;
    if (!currentToolboxScreenshot.has_team_selected) return true;
    return currentToolboxScreenshot.user_team?.team_code === userTeamCode;
  }, [currentToolboxScreenshot, userTeamCode]);

  const hiddenCanvasStyle = useMemo(() => ({
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    visibility: showOverlay ? 'hidden' : 'visible',
    transition: 'visibility 0.3s ease-in-out',
  }), [showOverlay]);

  const currentTeamAccentColor = getTeamAccentColor(userTeamCode);
  const formattedUserTeamScore = Number.isFinite(Number(userTeamScore))
    ? FormatNumberCounter(Number(userTeamScore))
    : userTeamScore || 0;

  const getScreenshotIdentifier = useCallback(() => {
    if (!currentToolboxScreenshot) return null;
    const teamIdentifier = currentToolboxScreenshot.has_team_selected
      ? currentToolboxScreenshot.user_team.team_code
      : 'individual';

    return `${currentToolboxScreenshot.id}-${teamIdentifier}`;
  }, [currentToolboxScreenshot]);

  // Socket effect for handling reactions
  useEffect(() => {
    if (!socket || !currentToolboxScreenshot) return;

    const handleReactionUpdate = (data, resetAll) => {
      const currentIdentifier = getScreenshotIdentifier();
      if (data.screenshotId !== currentIdentifier) return;

      if (resetAll) {
        setReactionCounts({
          like: 0,
          dislike: 0,
          tip: 0,
        })

        setMyReactions({
          like: false,
          dislike: false,
          tip: false,
        })
      } else {
        setReactionCounts(prev => {
          const newCounts = { ...prev };
          if (data.active) {
            newCounts[data.reactionType]++;
            if (data.reactionType === 'like' && data.previousReaction === 'dislike') {
              newCounts.dislike = Math.max(0, newCounts.dislike - 1);
            } else if (data.reactionType === 'dislike' && data.previousReaction === 'like') {
              newCounts.like = Math.max(0, newCounts.like - 1);
            }
          } else {
            newCounts[data.reactionType] = Math.max(0, newCounts[data.reactionType] - 1);
          }
          return newCounts;
        });

        if (data.userId === userData.id) {
          setMyReactions(prev => ({
            ...prev,
            [data.reactionType]: data.active
          }));
        }
      }

    };

    socket.on(SOCKET_EVENTS.SCREENSHOT_REACTION_UPDATE, handleReactionUpdate);

    socket.on(SOCKET_EVENTS.SCREENSHOT_EXPIRED, (data) => handleReactionUpdate(data, true));

    socket.emit(SOCKET_EVENTS.GET_SCREENSHOT_REACTIONS, {
      roomId,
      screenshotId: getScreenshotIdentifier(),
      userId: userData.id
    });

    return () => {
      socket.off(SOCKET_EVENTS.SCREENSHOT_REACTION_UPDATE, handleReactionUpdate);
    };
  }, [socket, currentToolboxScreenshot, roomId, getScreenshotIdentifier, userData.id]);

  // Reaction handler
  const handleScreenshotReaction = useCallback((type) => {
    if (!currentToolboxScreenshot || !socket) return;

    const screenshotId = getScreenshotIdentifier();
    if (!screenshotId) return;

    const teamData = currentToolboxScreenshot.has_team_selected
      ? {
        teamCode: currentToolboxScreenshot.user_team.team_code,
        teamName: currentToolboxScreenshot.user_team.team_name
      }
      : null;

    setMyReactions(prev => {
      const newReactions = { ...prev };
      if (type === 'like' || type === 'dislike') {
        if (type === 'like' && prev.dislike) newReactions.dislike = false;
        if (type === 'dislike' && prev.like) newReactions.like = false;
        newReactions[type] = !prev[type];
      } else if (type === 'tip') {
        newReactions.tip = !prev.tip;
      }
      pendingReactionsRef.current = newReactions;

      socket.emit(SOCKET_EVENTS.SCREENSHOT_REACTION_UPDATE, {
        roomId,
        screenshotId,
        teamData,
        userId: userData.id,
        reactionType: type,
        active: newReactions[type],
      });

      return newReactions;
    });
  }, [currentToolboxScreenshot, socket, roomId, getScreenshotIdentifier, userData.id]);

  // Function to save canvas states
  const saveCanvasStates = debounce(() => {
    const states = {
      draw: drawCanvasRef.current?.toDataURL(),
      text: textCanvasRef.current?.toDataURL(),
      image: imageCanvasRef.current?.toDataURL(),
      erase: eraseCanvasRef.current?.toDataURL(),
      teamDraw: teamDrawCanvasRef.current?.toDataURL(),
      teamText: teamTextCanvasRef.current?.toDataURL(),
      teamImage: teamImageCanvasRef.current?.toDataURL(),
      teamErase: teamEraseCanvasRef.current?.toDataURL()
    };
    setCanvasStates(states);
  }, 100);

  // Global mouseup: always stop drawing even when mouse released outside canvas
  useEffect(() => {
    const stopDrawing = () => { drawingRef.current = false; };
    window.addEventListener('mouseup', stopDrawing);
    return () => window.removeEventListener('mouseup', stopDrawing);
  }, []);

  // Webcam initialization effect
  useEffect(() => {
    const initWebcam = async () => {
      try {
        drawToCanvas();
      } catch (err) {
        console.error("Error accessing webcam:", err);
      }
    };

    const drawToCanvas = () => {
      const video = myVideoRef.current;
      const canvas = canvasVideoRef.current;

      if (canvas && video) {
        const ctx = canvas.getContext('2d');

        const updateCanvas = () => {
          if (video.readyState === video.HAVE_ENOUGH_DATA) {
            ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          }
          requestAnimationFrame(updateCanvas);
        };

        updateCanvas();
      }
    };

    initWebcam();
  }, []);

  // Team overlay drawing effect
  useEffect(() => {
    const drawTeamOverlay = () => {
      const canvas = teamOverlayCanvasRef.current;
      const video = myVideoRef.current;
      if (!canvas || !video) return;

      // Reset the canvas completely
      canvas.width = video.videoWidth || video.clientWidth;
      canvas.height = video.videoHeight || video.clientHeight;

      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      if (currentToolboxScreenshot &&
        (!currentToolboxScreenshot.has_team_selected ||
          currentToolboxScreenshot.user_team?.team_code === userTeamCode)) {
        const img = new Image();
        img.onload = () => {
          // Just draw the screenshot without any overlays
          ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
          setHasOverlayScreenshot(true);
        };
        img.src = currentToolboxScreenshot.screenshot;
      } else {
        setHasOverlayScreenshot(false);
      }
    };

    drawTeamOverlay();
  }, [currentToolboxScreenshot, userTeamCode, userData.username]);

  // Canvas resize effect
  useEffect(() => {
    const video = myVideoRef.current;
    const canvasVideo = canvasVideoRef.current;
    const drawCanvas = drawCanvasRef.current;
    const eraseCanvas = eraseCanvasRef.current;
    const textCanvas = textCanvasRef.current;
    const imageCanvas = imageCanvasRef.current;
    const compositeCanvas = compositeCanvasRef.current;
    const teamOverlayCanvas = teamOverlayCanvasRef.current;

    let resizeTimeout;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        clearTimeout(resizeTimeout);

        const { width, height } = entry.contentRect;

        const tempCanvases = {};
        const canvasRefs = {
          draw: drawCanvas,
          text: textCanvas,
          image: imageCanvas,
          erase: eraseCanvas,
          teamDraw: teamDrawCanvasRef.current,
          teamText: teamTextCanvasRef.current,
          teamImage: teamImageCanvasRef.current,
          teamErase: teamEraseCanvasRef.current
        };

        Object.entries(canvasRefs).forEach(([key, canvas]) => {
          if (!canvas) return;

          const tempCanvas = document.createElement('canvas');
          tempCanvas.width = canvas.width;
          tempCanvas.height = canvas.height;
          const tempCtx = tempCanvas.getContext('2d');
          tempCtx.drawImage(canvas, 0, 0);
          tempCanvases[key] = tempCanvas;
        });

        [canvasVideo, drawCanvas, eraseCanvas, textCanvas, imageCanvas,
          compositeCanvas, teamOverlayCanvas,
          teamDrawCanvasRef.current, teamTextCanvasRef.current,
          teamImageCanvasRef.current, teamEraseCanvasRef.current
        ].forEach(canvas => {
          if (canvas) {
            canvas.width = width;
            canvas.height = height;
          }
        });

        Object.entries(canvasRefs).forEach(([key, canvas]) => {
          if (!canvas || !tempCanvases[key]) return;

          const ctx = canvas.getContext('2d');
          ctx.lineWidth = lineThickness;
          ctx.strokeStyle = lineColor;
          ctx.globalAlpha = lineOpacity / 100;
          ctx.lineCap = "round";

          ctx.drawImage(
            tempCanvases[key],
            0, 0, tempCanvases[key].width, tempCanvases[key].height,
            0, 0, width, height
          );
        });

        Object.values(tempCanvases).forEach(tempCanvas => {
          tempCanvas.remove();
        });
      }
    });

    if (video) {
      resizeObserver.observe(video);
    }

    return () => {
      if (video) {
        resizeObserver.unobserve(video);
        clearTimeout(resizeTimeout);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineColor, lineOpacity, lineThickness]);

  // Socket event listener for spectator votes
  useEffect(() => {
    if (!socket) return;

    const handleVoteUpdate = (voteData) => {
      const isCurrentUserTeamRepresentative = userData.id?.toString() === voteData.teamRepresentingUserId?.toString();

      if (voteData.teamCode === userTeamCode || isCurrentUserTeamRepresentative) {
        setVoteCounts(voteData.counts);

        // Add the new vote to display list with timestamp
        setSpectatorVotes(prev => {
          const filteredVotes = prev.filter(v =>
            v.voterName !== voteData.voterName &&
            Date.now() - v.timestamp < 5000
          );
          return [...filteredVotes, {
            ...voteData,
            timestamp: Date.now()
          }];
        });

        // Update hasVoted state for the voter
        if (voteData.voterName === capitalizeEachFirstWord(userData.username)) {
          setHasVoted(voteData.voteType);
        }
      }
    };

    socket.on(SOCKET_EVENTS.SPECTATOR_VOTE, handleVoteUpdate);
    socket.on(SOCKET_EVENTS.SHUFFLE_VIDEO_CALL_IN_ROOM, () => {
      setVoteCounts({ up: 0, down: 0 });
      setHasVoted(null);
      setSpectatorVotes([]);
    });

    return () => {
      socket.off(SOCKET_EVENTS.SPECTATOR_VOTE, handleVoteUpdate);
      socket.off(SOCKET_EVENTS.SHUFFLE_VIDEO_CALL_IN_ROOM);
    };
  }, [socket, userTeamCode, userData.username]);

  // Reaction Button Component
  const ReactionButton = ({ type, icon, active, count }) => (
    <Tooltip title={`${count} ${type}${count !== 1 ? 's' : ''}`}>
      <Badge
        badgeContent={count}
        color="primary"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        <IconButton
          onClick={() => handleScreenshotReaction(type)}
          size="small"
        >
          {React.cloneElement(icon, {
            style: {
              fontSize: '13px',
              color: active ? 'gold' : 'whitesmoke'
            }
          })}
        </IconButton>
      </Badge>
    </Tooltip>
  );

  // Text canvas effect
  useEffect(() => {
    const canvas = selectedTeam ? teamTextCanvasRef.current : textCanvasRef.current;

    if (canvas) {
      const ctx = canvas.getContext('2d');

      const handleMouseDown = (e) => {
        const rect = canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        let clickedOnText = false;

        if (selectedTeam) {
          for (let i = 0; i < TeamTextCanvas.length; i++) {
            if (textHittest(mouseX, mouseY, i)) {
              setSelectedTeamTextCanvas(i);
              setStartTeamTextCanvasX(mouseX - TeamTextCanvas[i].x);
              setStartTeamTextCanvasY(mouseY - TeamTextCanvas[i].y);
              setIsDraggingTeamTextCanvas(true);
              clickedOnText = true;
              break;
            }
          }
        } else {
          for (let i = 0; i < TextCanvas.length; i++) {
            if (textHittest(mouseX, mouseY, i)) {
              setSelectedTextCanvas(i);
              setStartTextCanvasX(mouseX - TextCanvas[i].x);
              setStartTextCanvasY(mouseY - TextCanvas[i].y);
              setIsDraggingTextCanvas(true);
              clickedOnText = true;
              break;
            }
          }
        }

        const isClickOutsideToolbox = !toolboxRef?.current?.contains(e.target);

        if (!clickedOnText && isClickOutsideToolbox) {
          if (selectedTeam) {
            if (selectedTeamTextCanvas >= 0 && TeamTextCanvas[selectedTeamTextCanvas]?.text.trim() === '') {
              setTeamTextCanvas((prevTexts) =>
                prevTexts.filter((_, index) => index !== selectedTeamTextCanvas)
              );
            }
            setSelectedTeamTextCanvas(-1);
          } else {
            if (selectedTextCanvas >= 0 && TextCanvas[selectedTextCanvas]?.text.trim() === '') {
              setTextCanvas((prevTexts) =>
                prevTexts.filter((_, index) => index !== selectedTextCanvas)
              );
            }
            setSelectedTextCanvas(-1);
          }
        }
      };

      const handleMouseUp = () => {
        if (selectedTeam) {
          setIsDraggingTeamTextCanvas(false);
        } else {
          setIsDraggingTextCanvas(false);
        }
      };

      const handleMouseOut = () => {
        if (selectedTeam) {
          setIsDraggingTeamTextCanvas(false);
        } else {
          setIsDraggingTextCanvas(false);
        }
      };

      const handleMouseMove = (e) => {
        e.preventDefault();

        if (selectedTeam) {
          if (!isDraggingTeamTextCanvas || selectedTeamTextCanvas < 0) return;
        } else {
          if (!isDraggingTextCanvas || selectedTextCanvas < 0) return;
        }

        const rect = canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        let newX = selectedTeam ? mouseX - startTeamTextCanvasX : mouseX - startTextCanvasX;
        let newY = selectedTeam ? mouseY - startTeamTextCanvasY : mouseY - startTextCanvasY;

        if (newX < 0) newX = 0;

        if (selectedTeam) {
          if (newX + TeamTextCanvas[selectedTeamTextCanvas].width > canvas.width) {
            newX = canvas.width - TeamTextCanvas[selectedTeamTextCanvas].width;
          }
          if (newY < TeamTextCanvas[selectedTeamTextCanvas].height) {
            newY = TeamTextCanvas[selectedTeamTextCanvas].height;
          }
          if (newY > canvas.height) {
            newY = canvas.height;
          }

          setTeamTextCanvas((prevTexts) => {
            const newTexts = [...prevTexts];
            const selectedText = newTexts[selectedTeamTextCanvas];
            selectedText.x = newX;
            selectedText.y = newY;
            return newTexts;
          });
        } else {
          if (newX + TextCanvas[selectedTextCanvas].width > canvas.width) {
            newX = canvas.width - TextCanvas[selectedTextCanvas].width;
          }
          if (newY < TextCanvas[selectedTextCanvas].height) {
            newY = TextCanvas[selectedTextCanvas].height;
          }
          if (newY > canvas.height) {
            newY = canvas.height;
          }

          setTextCanvas((prevTexts) => {
            const newTexts = [...prevTexts];
            const selectedText = newTexts[selectedTextCanvas];
            selectedText.x = newX;
            selectedText.y = newY;
            return newTexts;
          });
        }

        draw(ctx);
        saveCanvasStates();
      };

      const textHittest = (x, y, textIndex) => {
        const text = selectedTeam ? TeamTextCanvas[textIndex] : TextCanvas[textIndex];
        const { x: textX, y: textY, width, height } = text;
        return (
          x >= textX &&
          x <= textX + width &&
          y >= textY - height &&
          y <= textY
        );
      };

      const draw = (ctx) => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        if (selectedTeam) {
          TeamTextCanvas.forEach((text, index) => {
            ctx.save();
            ctx.translate(text.x, text.y);
            ctx.rotate((text.angle * Math.PI) / 180);
            ctx.font = `${text.fontStyle} ${text.fontSize}px ${text.fontFamily} `;
            ctx.fillStyle = text.color;
            ctx.fillText(text.text, 0, 0);

            const textMetrics = ctx.measureText(text.text);
            text.width = textMetrics.width;
            text.height = text.fontSize;

            if (index === selectedTeamTextCanvas) {
              ctx.strokeStyle = 'tomato';
              const padding = 5;
              ctx.strokeRect(-padding, -text.height - padding, text.width + padding * 2, text.height + padding * 2);
            }
            ctx.restore();
          });
        } else {
          TextCanvas.forEach((text, index) => {
            ctx.save();
            ctx.translate(text.x, text.y);
            ctx.rotate((text.angle * Math.PI) / 180);
            ctx.font = `${text.fontStyle} ${text.fontSize}px ${text.fontFamily} `;
            ctx.fillStyle = text.color;
            ctx.fillText(text.text, 0, 0);

            const textMetrics = ctx.measureText(text.text);
            text.width = textMetrics.width;
            text.height = text.fontSize;

            if (index === selectedTextCanvas) {
              ctx.strokeStyle = 'tomato';
              const padding = 5;
              ctx.strokeRect(-padding, -text.height - padding, text.width + padding * 2, text.height + padding * 2);
            }
            ctx.restore();
          });
        }
      }

      canvas.addEventListener('mousedown', handleMouseDown);
      canvas.addEventListener('mousemove', handleMouseMove);
      canvas.addEventListener('mouseup', handleMouseUp);
      canvas.addEventListener('mouseout', handleMouseOut);

      draw(ctx);

      return () => {
        canvas.removeEventListener('mousedown', handleMouseDown);
        canvas.removeEventListener('mousemove', handleMouseMove);
        canvas.removeEventListener('mouseup', handleMouseUp);
        canvas.removeEventListener('mouseout', handleMouseOut);
      };
    }
  }, [TeamTextCanvas, TextCanvas, selectedTeamTextCanvas, selectedTextCanvas, startTeamTextCanvasX,
    startTextCanvasX, startTeamTextCanvasY, startTextCanvasY, isDraggingTeamTextCanvas,
    isDraggingTextCanvas, toolboxMode, selectedTeam, saveCanvasStates]);


  // Text rotation effect
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'ArrowLeft') {
        updateSelectedTextAngle(-1);
        saveCanvasStates();
      } else if (e.key === 'ArrowRight') {
        updateSelectedTextAngle(1);
        saveCanvasStates();
      }
    };

    const updateSelectedTextAngle = (deltaAngle) => {
      if (selectedTeam) {
        if (selectedTeamTextCanvas >= 0) {
          setTeamTextCanvas((prevTexts) => {
            const newTexts = [...prevTexts];
            newTexts[selectedTeamTextCanvas] = {
              ...newTexts[selectedTeamTextCanvas],
              angle: newTexts[selectedTeamTextCanvas].angle + deltaAngle,
            };
            return newTexts;
          });
        }
      } else {
        if (selectedTextCanvas >= 0) {
          setTextCanvas((prevTexts) => {
            const newTexts = [...prevTexts];
            newTexts[selectedTextCanvas] = {
              ...newTexts[selectedTextCanvas],
              angle: newTexts[selectedTextCanvas].angle + deltaAngle,
            };
            return newTexts;
          });
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [selectedTeamTextCanvas, selectedTextCanvas, selectedTeam, saveCanvasStates]);

  // Image canvas effect
  useEffect(() => {
    const canvas = selectedTeam ? teamImageCanvasRef.current : imageCanvasRef.current;

    if (canvas) {
      const ctx = canvas.getContext('2d');

      const drawImages = () => {
        ctx.clearRect(0, 0, canvas.width, canvas.height);

        const images = selectedTeam ? teamImageCanvas : imageCanvas;
        const selectedIndex = selectedTeam ? selectedTeamImageCanvasIndex : selectedImageCanvasIndex;

        images.forEach((image, index) => {
          const img = new Image();
          img.src = image.url;
          img.onload = () => {
            ctx.drawImage(img, image.x, image.y, image.width, image.height);
            if (index === selectedIndex) {
              ctx.strokeStyle = 'blue';
              ctx.strokeRect(image.x, image.y, image.width, image.height);

              const handleSize = 10;
              ctx.fillStyle = 'blue';
              ctx.fillRect(image.x - handleSize / 2, image.y - handleSize / 2, handleSize, handleSize);
              ctx.fillRect(image.x + image.width - handleSize / 2, image.y - handleSize / 2, handleSize, handleSize);
              ctx.fillRect(image.x - handleSize / 2, image.y + image.height - handleSize / 2, handleSize, handleSize);
              ctx.fillRect(image.x + image.width - handleSize / 2, image.y + image.height - handleSize / 2, handleSize, handleSize);
            }
          };
        });
      };

      const handleMouseDown = (e) => {
        const rect = canvas.getBoundingClientRect();
        const offsetX = e.clientX - rect.left;
        const offsetY = e.clientY - rect.top;
        const images = selectedTeam ? teamImageCanvas : imageCanvas;
        const clickedImageIndex = images.findIndex(
          (image) =>
            offsetX >= image.x &&
            offsetX <= image.x + image.width &&
            offsetY >= image.y &&
            offsetY <= image.y + image.height
        );

        if (selectedTeam) {
          setSelectedTeamImageCanvasIndex(clickedImageIndex);
        } else {
          setSelectedImageCanvasIndex(clickedImageIndex);
        }

        if (clickedImageIndex !== -1) {
          const selectedImage = images[clickedImageIndex];
          const handleSize = 10;
          if (
            offsetX >= selectedImage.x - handleSize / 2 &&
            offsetX <= selectedImage.x + handleSize / 2 &&
            offsetY >= selectedImage.y - handleSize / 2 &&
            offsetY <= selectedImage.y + handleSize / 2
          ) {
            setIsResizingImageCanvas(true);
            setResizeHandleImageCanvas('topLeft');
          } else if (
            offsetX >= selectedImage.x + selectedImage.width - handleSize / 2 &&
            offsetX <= selectedImage.x + selectedImage.width + handleSize / 2 &&
            offsetY >= selectedImage.y - handleSize / 2 &&
            offsetY <= selectedImage.y + handleSize / 2
          ) {
            setIsResizingImageCanvas(true);
            setResizeHandleImageCanvas('topRight');
          } else if (
            offsetX >= selectedImage.x - handleSize / 2 &&
            offsetX <= selectedImage.x + handleSize / 2 &&
            offsetY >= selectedImage.y + selectedImage.height - handleSize / 2 &&
            offsetY <= selectedImage.y + selectedImage.height + handleSize / 2
          ) {
            setIsResizingImageCanvas(true);
            setResizeHandleImageCanvas('bottomLeft');
          } else if (
            offsetX >= selectedImage.x + selectedImage.width - handleSize / 2 &&
            offsetX <= selectedImage.x + selectedImage.width + handleSize / 2 &&
            offsetY >= selectedImage.y + selectedImage.height - handleSize / 2 &&
            offsetY <= selectedImage.y + selectedImage.height + handleSize / 2
          ) {
            setIsResizingImageCanvas(true);
            setResizeHandleImageCanvas('bottomRight');
          } else {
            setIsDraggingImageCanvas(true);
          }
        }
      };

      const handleMouseMove = (e) => {
        if (isDraggingImageCanvas || isResizingImageCanvas) {
          const rect = canvas.getBoundingClientRect();
          const offsetX = e.clientX - rect.left;
          const offsetY = e.clientY - rect.top;
          const images = selectedTeam ? teamImageCanvas : imageCanvas;
          const selectedIndex = selectedTeam ? selectedTeamImageCanvasIndex : selectedImageCanvasIndex;

          if (isDraggingImageCanvas && selectedIndex !== -1) {
            const updatedImages = [...images];
            const selectedImage = updatedImages[selectedIndex];
            const newX = offsetX - selectedImage.width / 2;
            const newY = offsetY - selectedImage.height / 2;
            selectedImage.x = Math.max(0, Math.min(newX, canvas.width - selectedImage.width));
            selectedImage.y = Math.max(0, Math.min(newY, canvas.height - selectedImage.height));

            if (selectedTeam) {
              setTeamImageCanvas(updatedImages);
            } else {
              setImageCanvas(updatedImages);
            }
          } else if (isResizingImageCanvas && selectedIndex !== -1) {
            const updatedImages = [...images];
            const selectedImage = updatedImages[selectedIndex];
            if (resizeHandleImageCanvas === 'topLeft') {
              const newWidth = selectedImage.width + selectedImage.x - offsetX;
              const newHeight = selectedImage.height + selectedImage.y - offsetY;
              selectedImage.width = Math.max(20, Math.min(newWidth, selectedImage.x + selectedImage.width));
              selectedImage.height = Math.max(20, Math.min(newHeight, selectedImage.y + selectedImage.height));
              selectedImage.x = offsetX;
              selectedImage.y = offsetY;
            } else if (resizeHandleImageCanvas === 'topRight') {
              const newWidth = offsetX - selectedImage.x;
              const newHeight = selectedImage.height + selectedImage.y - offsetY;
              selectedImage.width = Math.max(20, Math.min(newWidth, canvas.width - selectedImage.x));
              selectedImage.height = Math.max(20, Math.min(newHeight, selectedImage.y + selectedImage.height));
              selectedImage.y = offsetY;
            } else if (resizeHandleImageCanvas === 'bottomLeft') {
              const newWidth = selectedImage.width + selectedImage.x - offsetX;
              const newHeight = offsetY - selectedImage.y;
              selectedImage.width = Math.max(20, Math.min(newWidth, selectedImage.x + selectedImage.width));
              selectedImage.height = Math.max(20, Math.min(newHeight, canvas.height - selectedImage.y));
              selectedImage.x = offsetX;
            } else if (resizeHandleImageCanvas === 'bottomRight') {
              const newWidth = offsetX - selectedImage.x;
              const newHeight = offsetY - selectedImage.y;
              selectedImage.width = Math.max(20, Math.min(newWidth, canvas.width - selectedImage.x));
              selectedImage.height = Math.max(20, Math.min(newHeight, canvas.height - selectedImage.y));
            }

            if (selectedTeam) {
              setTeamImageCanvas(updatedImages);
            } else {
              setImageCanvas(updatedImages);
            }
          }

          saveCanvasStates();
        }
      };

      const handleMouseUp = () => {
        setIsDraggingImageCanvas(false);
        setIsResizingImageCanvas(false);
        setResizeHandleImageCanvas(null);
      };

      const handleMouseOut = () => {
        setIsDraggingImageCanvas(false);
        setIsResizingImageCanvas(false);
        setResizeHandleImageCanvas(null);
      };

      canvas.addEventListener('mousedown', handleMouseDown);
      canvas.addEventListener('mousemove', handleMouseMove);
      canvas.addEventListener('mouseup', handleMouseUp);
      canvas.addEventListener('mouseout', handleMouseOut);

      const animationId = requestAnimationFrame(drawImages);

      return () => {
        canvas.removeEventListener('mousedown', handleMouseDown);
        canvas.removeEventListener('mousemove', handleMouseMove);
        canvas.removeEventListener('mouseup', handleMouseUp);
        canvas.removeEventListener('mouseout', handleMouseOut);
        cancelAnimationFrame(animationId);

        if (toolboxMode !== 'imageAndIcons') {
          if (selectedTeam) {
            setSelectedTeamImageCanvasIndex(-1);
          } else {
            setSelectedImageCanvasIndex(-1);
          }
        }
      };
    }
  }, [selectedTeam, teamImageCanvas, imageCanvas, selectedTeamImageCanvasIndex, selectedImageCanvasIndex,
    isDraggingImageCanvas, isResizingImageCanvas, resizeHandleImageCanvas, toolboxMode, saveCanvasStates]);


  // Toolbox mode and selection effect
  useEffect(() => {
    if (selectedTeam) {
      if (selectedTeamTextCanvas >= 0 && toolboxMode !== 'text') {
        setSelectedTeamTextCanvas(-1);
      }
      if (selectedTeamImageCanvasIndex >= 0 && toolboxMode !== 'imageAndIcons') {
        setSelectedTeamImageCanvasIndex(-1);
      }
    } else {
      if (selectedTextCanvas >= 0 && toolboxMode !== 'text') {
        setSelectedTextCanvas(-1);
      }
      if (selectedImageCanvasIndex >= 0 && toolboxMode !== 'imageAndIcons') {
        setSelectedImageCanvasIndex(-1);
      }
    }
  }, [toolboxMode, selectedTeam, selectedTeamTextCanvas, selectedTeamImageCanvasIndex, selectedTextCanvas, selectedImageCanvasIndex]);

  // Team selection effect
  useEffect(() => {
    if (selectedTeam) {
      setSelectedTextCanvas(-1);
      setSelectedImageCanvasIndex(-1);
    } else {
      setSelectedTeamTextCanvas(-1);
      setSelectedTeamImageCanvasIndex(-1);
    }
  }, [selectedTeam]);

  // Active team participants effect
  useEffect(() => {
    if (activeTeamParticipantVideos.length <= 0) {
      setSelectedTeam(null)
    }
  }, [activeTeamParticipantVideos])

  // Handler functions
  const removeSelectedText = (isTeam) => {
    if (isTeam) {
      if (selectedTeamTextCanvas >= 0) {
        setTeamTextCanvas((prevTexts) => prevTexts.filter((_, index) => index !== selectedTeamTextCanvas));
        setSelectedTeamTextCanvas(-1);
      }
    } else {
      if (selectedTextCanvas >= 0) {
        setTextCanvas((prevTexts) => prevTexts.filter((_, index) => index !== selectedTextCanvas));
        setSelectedTextCanvas(-1);
      }
    }
  };

  const handleStartDrawing = (e) => {
    if (toolboxMode === "draw" || toolboxMode === "erase") {
      e.nativeEvent.stopPropagation();
      drawingRef.current = true;
      positionRef.current = { x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY };
    }
  };

  const handleDraw = useCallback((e) => {
    if (toolboxMode === "draw") {
      if (!drawingRef.current) return;
      const canvas = selectedTeam ? teamDrawCanvasRef.current : drawCanvasRef.current;

      if (canvas) {
        const ctx = canvas.getContext('2d');
        ctx.globalCompositeOperation = 'source-over';
        ctx.beginPath();
        ctx.strokeStyle = lineColor;
        ctx.lineWidth = lineThickness;
        ctx.globalAlpha = lineOpacity / 100;
        ctx.lineCap = "round";
        ctx.moveTo(positionRef.current.x, positionRef.current.y);
        ctx.lineTo(e.nativeEvent.offsetX, e.nativeEvent.offsetY);
        ctx.stroke();

        positionRef.current = { x: e.nativeEvent.offsetX, y: e.nativeEvent.offsetY };
        saveCanvasStates();
      }
    }
  }, [lineColor, lineOpacity, lineThickness, saveCanvasStates, selectedTeam, toolboxMode]);

  const handleStopDrawing = useCallback(() => {
    drawingRef.current = false;
  }, []);

  const doErasing = (e) => {
    if (toolboxMode === 'erase' && drawingRef.current) {
      const eraseCanvas = selectedTeam ? teamEraseCanvasRef.current : eraseCanvasRef.current;
      const drawCanvas = selectedTeam ? teamDrawCanvasRef.current : drawCanvasRef.current;

      if (eraseCanvas && drawCanvas) {
        const eraseCTX = eraseCanvas.getContext('2d');
        const drawCTX = drawCanvas.getContext('2d');

        let size = lineThickness * 2;
        const x = e.nativeEvent.offsetX - size / 2;
        const y = e.nativeEvent.offsetY - size / 2;

        eraseCTX.globalCompositeOperation = 'destination-out';
        eraseCTX.fillRect(x, y, size, size);

        drawCTX.globalCompositeOperation = 'destination-out';
        drawCTX.fillRect(x, y, size, size);

        saveCanvasStates();
      }
    }
  };

  const handleImageSelect = (imageUrl) => {
    const newImage = {
      url: imageUrl,
      x: 100,
      y: 100,
      width: 100,
      height: 100,
    };
    if (selectedTeam) {
      setTeamImageCanvas([...teamImageCanvas, newImage]);
      setSelectedTeamImageCanvasIndex(teamImageCanvas.length);
    } else {
      setImageCanvas([...imageCanvas, newImage]);
      setSelectedImageCanvasIndex(imageCanvas.length);
    }

    saveCanvasStates();
  };

  const saveCanvasDrawing = () => {
    setSelectedTextCanvas(-1);
    setSelectedImageCanvasIndex(-1);
    setSelectedTeamTextCanvas(-1);
    setSelectedTeamImageCanvasIndex(-1);

    const compositeCanvas = selectedTeam ? teamCompositeCanvasRef.current : compositeCanvasRef.current;
    const drawCanvas = selectedTeam ? teamDrawCanvasRef.current : drawCanvasRef.current;
    const textCanvas = selectedTeam ? teamTextCanvasRef.current : textCanvasRef.current;
    const imageCanvas = selectedTeam ? teamImageCanvasRef.current : imageCanvasRef.current;
    const eraseCanvas = selectedTeam ? teamEraseCanvasRef.current : eraseCanvasRef.current;

    if (compositeCanvas && drawCanvas && textCanvas && imageCanvas && eraseCanvas) {
      const compositeCtx = compositeCanvas.getContext('2d');

      compositeCtx.clearRect(0, 0, compositeCanvas.width, compositeCanvas.height);

      if (drawCanvas.width > 0 && drawCanvas.height > 0) {
        compositeCtx.drawImage(drawCanvas, 0, 0);
      }

      if (textCanvas.width > 0 && textCanvas.height > 0) {
        compositeCtx.drawImage(textCanvas, 0, 0);
      }

      if (imageCanvas.width > 0 && imageCanvas.height > 0) {
        compositeCtx.drawImage(imageCanvas, 0, 0);
      }

      if (eraseCanvas.width > 0 && eraseCanvas.height > 0) {
        compositeCtx.drawImage(eraseCanvas, 0, 0);
      }

      const link = document.createElement('a');
      link.download = 'canvas-drawing.png';
      link.href = compositeCanvas.toDataURL('image/png');
      link.click();
    }
  };

  const saveScreenshot = (sendScreenshot, durationInSecs) => {
    setSelectedTextCanvas(-1);
    setSelectedImageCanvasIndex(-1);
    setSelectedTeamTextCanvas(-1);
    setSelectedTeamImageCanvasIndex(-1);

    setTimeout(() => {
      const compositeCanvas = selectedTeam ? teamCompositeCanvasRef.current : compositeCanvasRef.current;
      const drawCanvas = selectedTeam ? teamDrawCanvasRef.current : drawCanvasRef.current;
      const textCanvas = selectedTeam ? teamTextCanvasRef.current : textCanvasRef.current;
      const imageCanvas = selectedTeam ? teamImageCanvasRef.current : imageCanvasRef.current;
      const eraseCanvas = selectedTeam ? teamEraseCanvasRef.current : eraseCanvasRef.current;

      if (compositeCanvas && drawCanvas && textCanvas && imageCanvas && eraseCanvas) {
        compositeCanvas.style.display = 'block';

        const compositeCtx = compositeCanvas.getContext('2d');

        compositeCtx.clearRect(0, 0, compositeCanvas.width, compositeCanvas.height);

        const captureAndComposite = () => {
          if (drawCanvas.width > 0 && drawCanvas.height > 0) {
            compositeCtx.drawImage(drawCanvas, 0, 0);
          }

          if (textCanvas.width > 0 && textCanvas.height > 0) {
            compositeCtx.drawImage(textCanvas, 0, 0);
          }

          if (imageCanvas.width > 0 && imageCanvas.height > 0) {
            compositeCtx.drawImage(imageCanvas, 0, 0);
          }

          if (eraseCanvas.width > 0 && eraseCanvas.height > 0) {
            compositeCtx.drawImage(eraseCanvas, 0, 0);
          }

          if (sendScreenshot && durationInSecs) {
            const screenshotDataURL = compositeCanvas.toDataURL('image/png');

            const hasTeamSelected = selectedTeam ? true : false;
            const userTeam = hasTeamSelected ? {
              team_code: selectedTeam.userTeamCode,
              team_name: selectedTeam.userTeamName,
              team_representing_user_id: selectedTeam.userId
            } : null;

            handleSendToolboxScreenshot(screenshotDataURL, durationInSecs, hasTeamSelected, userTeam);

            compositeCanvas.style.display = 'none';
          } else {
            const link = document.createElement('a');
            link.download = 'composite-image.png';
            link.href = compositeCanvas.toDataURL('image/png');

            const handleFocus = () => {
              compositeCanvas.style.display = 'none';
              window.removeEventListener('focus', handleFocus);
            };

            window.addEventListener('focus', handleFocus);

            link.click();
          }
        };

        captureAndComposite();
      }
    }, 1000);
  };


  const addTextToCanvas = (isTeam) => {
    if (isTeam) {
      if (!teamTextProps.text) return;

      const ctx = teamTextCanvasRef.current.getContext('2d');
      const y = TeamTextCanvas.length * 20 + 20;

      ctx.font = `${teamTextProps.fontStyle} ${teamTextProps.fontSize}px ${teamTextProps.fontFamily} `;
      const textWidth = ctx.measureText(teamTextProps.text).width;
      const textHeight = teamTextProps.fontSize;

      const newText = {
        ...teamTextProps,
        x: 20,
        y: y,
        width: textWidth,
        height: textHeight,
      };

      setTeamTextCanvas((prevTexts) => [...prevTexts, newText]);
      setTeamTextProps((prev) => ({ ...prev, text: "" }));
      saveCanvasStates();
    } else {
      if (!textProps.text) return;

      const ctx = textCanvasRef.current.getContext('2d');
      const y = TextCanvas.length * 20 + 20;

      ctx.font = `${textProps.fontStyle} ${textProps.fontSize}px ${textProps.fontFamily} `;
      const textWidth = ctx.measureText(textProps.text).width;
      const textHeight = textProps.fontSize;

      const newText = {
        ...textProps,
        x: 20,
        y: y,
        width: textWidth,
        height: textHeight,
      };

      setTextCanvas((prevTexts) => [...prevTexts, newText]);
      setTextProps((prev) => ({ ...prev, text: "" }));
      saveCanvasStates();
    }
  };

  const clearCanvas = () => {
    if (selectedTeam) {
      const teamDrawCanvas = teamDrawCanvasRef.current;
      const teamTextCanvas = teamTextCanvasRef.current;
      const teamImageCanvas = teamImageCanvasRef.current;
      const teamEraseCanvas = teamEraseCanvasRef.current;

      if (teamDrawCanvas && teamTextCanvas && teamImageCanvas && teamEraseCanvas) {
        const drawCtx = teamDrawCanvas.getContext('2d');
        const textCtx = teamTextCanvas.getContext('2d');
        const imageCtx = teamImageCanvas.getContext('2d');
        const eraseCtx = teamEraseCanvas.getContext('2d');

        drawCtx.clearRect(0, 0, teamDrawCanvas.width, teamDrawCanvas.height);
        textCtx.clearRect(0, 0, teamTextCanvas.width, teamTextCanvas.height);
        imageCtx.clearRect(0, 0, teamImageCanvas.width, teamImageCanvas.height);
        eraseCtx.clearRect(0, 0, teamEraseCanvas.width, teamEraseCanvas.height);

        setTeamTextCanvas([]);
        setSelectedTeamTextCanvas(-1);
        setTeamImageCanvas([]);
        setSelectedTeamImageCanvasIndex(-1);
      }
    } else {
      const drawCanvas = drawCanvasRef.current;
      const textCanvas = textCanvasRef.current;
      const imageCanvas = imageCanvasRef.current;
      const eraseCanvas = eraseCanvasRef.current;

      if (drawCanvas && textCanvas && imageCanvas && eraseCanvas) {
        const drawCtx = drawCanvas.getContext('2d');
        const textCtx = textCanvas.getContext('2d');
        const imageCtx = imageCanvas.getContext('2d');
        const eraseCtx = eraseCanvas.getContext('2d');

        drawCtx.clearRect(0, 0, drawCanvas.width, drawCanvas.height);
        textCtx.clearRect(0, 0, textCanvas.width, textCanvas.height);
        imageCtx.clearRect(0, 0, imageCanvas.width, imageCanvas.height);
        eraseCtx.clearRect(0, 0, eraseCanvas.width, eraseCanvas.height);

        setTextCanvas([]);
        setSelectedTextCanvas(-1);
        setImageCanvas([]);
        setSelectedImageCanvasIndex(-1);
      }
    }

    saveCanvasStates();
  };

  const changeSelectedTeam = (e) => {
    const selectedTeamCode = e.target.value;
    const newSelectedTeam = activeTeamParticipantVideos.find(
      (participant) => participant.userTeamCode === selectedTeamCode
    );

    if (newSelectedTeam) {
      setSelectedTextCanvas(-1);
      setSelectedImageCanvasIndex(-1);
    } else {
      setSelectedTeamTextCanvas(-1);
      setSelectedTeamImageCanvasIndex(-1);
    }

    setSelectedTeam(newSelectedTeam);
  };

  const removeSelectedTeam = () => {
    setSelectedTeam(null);
  };

  const handleToolboxModeChange = (newMode) => {
    if (newMode !== toolboxMode) {
      if (selectedTeam) {
        setSelectedTeamTextCanvas(-1);
        setSelectedTeamImageCanvasIndex(-1);
      } else {
        setSelectedTextCanvas(-1);
        setSelectedImageCanvasIndex(-1);
      }
    }
    setToolboxMode(newMode);
  };

  // Render section
  return (
    <React.Fragment>
      <div className={displayVideo ? '' : classes.videoCamBoxContainer}>
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ position: 'relative', display: 'flex' }}>
            {userTeamCode && !['admin', 'team-spectator'].includes(userTeamCode) && (
              <div
                style={{
                  position: 'absolute',
                  top: '8px',
                  left: '8px',
                  zIndex: 1200,
                  background: 'rgba(0, 0, 0, 0.88)',
                  color: currentTeamAccentColor,
                  border: `1px solid ${currentTeamAccentColor}`,
                  borderRadius: '8px',
                  padding: '4px 8px',
                  fontSize: 'clamp(8px, 1vw, 14px)',
                  fontWeight: 700,
                  lineHeight: 1.2,
                  pointerEvents: 'none',
                  maxWidth: 'calc(100% - 16px)',
                }}
              >
                {`${capitalizeEachFirstWord(userTeamName)} • ${formattedUserTeamScore}`}
              </div>
            )}
            <div style={{ position: 'relative' }}>
              <video
                ref={myVideoRef}
                className={`${classes.videoCam} ${userTeamCode}`}
                muted
                autoPlay
                playsInline
                style={{ zIndex: 0, width: '100%', height: 'auto' }}
              />
            </div>

            <canvas
              id="teamOverlayCanvas"
              ref={teamOverlayCanvasRef}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                zIndex: hasOverlayScreenshot ? 10 : -1,
                pointerEvents: 'none',
              }}
            />

            {currentToolboxScreenshot && (
              <div style={{
                position: 'absolute',
                bottom: '9px',
                left: 4,
                width: '100%',
                pointerEvents: 'auto',
                zIndex: 1500,
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  padding: '8px',
                  height: '30px',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  color: 'white',
                  fontFamily: 'Arial, sans-serif',
                  fontSize: '10px',
                  width: 'calc(100% - 15px)',
                  margin: 'auto',
                }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '1px',
                  }}>
                    <div>
                      {currentToolboxScreenshot.user_team
                        ? `Team: ${capitalizeEachFirstWord(currentToolboxScreenshot.user_team.team_name)}`
                        : ''}
                    </div>
                    <span>Sent by: {userData.username.toLowerCase() === currentToolboxScreenshot.username.toLowerCase() ? 'You' : currentToolboxScreenshot.username}</span>
                  </div>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                      <ReactionButton
                        type="like"
                        icon={<ThumbUpIcon />}
                        active={myReactions.like}
                        count={reactionCounts.like}
                      />
                      <ReactionButton
                        type="dislike"
                        icon={<ThumbDownIcon />}
                        active={myReactions.dislike}
                        count={reactionCounts.dislike}
                      />
                      <ReactionButton
                        type="tip"
                        icon={<MonetizationOnIcon />}
                        active={myReactions.tip}
                        count={reactionCounts.tip}
                      />
                    </div>
                    <div style={{ fontSize: '12px', fontWeight: 'bold' }}>{countdown}s</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Display spectator votes */}
      <div className={classes.spectatorVotesContainer}>
        {spectatorVotes.map((vote) => (
          <div key={vote.timestamp} className={classes.voteContainer}>
            <span className={classes.voteIcon}>
              {vote.voteType === 'up' ? '👍' : '👎'}
            </span>
            <span className={classes.voteDescription}>
              {`${vote.voterName} ${vote.voteType === 'up' ? 'upvoted' : 'downvoted'} ${vote.teamRepresentingUserId?.toString() === userData.id?.toString()
                  ? 'your team'
                  : capitalizeEachFirstWord(vote.teamName)
                }`}
            </span>
          </div>
        ))}
      </div>

      {roomToolbox.isOpen && roomToolbox.currentUserToolboxEnabled ? (
        <DraggableToolbox
          clearCanvas={clearCanvas}
          saveCanvasDrawing={saveCanvasDrawing}
          saveScreenshot={saveScreenshot}
          toolboxMode={toolboxMode}
          lineColor={lineColor}
          colorPickerRef={colorPickerRef}
          setLineColor={setLineColor}
          lineThickness={lineThickness}
          setLineThickness={setLineThickness}
          opacitySliderRef={opacitySliderRef}
          lineOpacity={lineOpacity}
          setLineOpacity={setLineOpacity}
          classes={classes}
          isVideoOn={isVideoOn}
          handleVideoToggle={handleVideoToggle}
          isAudioOn={isAudioOn}
          handleAudioToggle={handleAudioToggle}
          showRoomParticipants={showRoomParticipants}
          handleRoomParticipantsVisibility={handleRoomParticipantsVisibility}
          handleReactionIconExpandClick={handleReactionIconExpandClick}
          reactionIconExpanded={reactionIconExpanded}
          handleWebcamMenuClick={handleWebcamMenuClick}
          anchorWebcamMenuEl={anchorWebcamMenuEl}
          handleWebcamMenuClose={handleWebcamMenuClose}
          openUserWebcamSettingsModal={openUserWebcamSettingsModal}
          handleRoomTitleToggle={handleRoomTitleToggle}
          showRoomTitle={showRoomTitle}
          handleRoomTeamsVisibility={handleRoomTeamsVisibility}
          showRoomTeams={showRoomTeams}
          loadANewPage={loadANewPage}
          roomId={roomId}
          togglingRoomPath={togglingRoomPath}
          isUserOnSpectatorMode={isUserOnSpectatorMode}
          handleReactionIcon={handleReactionIcon}
          classes2={classes2}
          textProps={textProps}
          setTextProps={setTextProps}
          addTextToCanvas={addTextToCanvas}
          selectedTextCanvas={selectedTextCanvas}
          TextCanvas={TextCanvas}
          setTextCanvas={setTextCanvas}
          removeSelectedText={removeSelectedText}
          toolboxRef={toolboxRef}
          onImageSelect={handleImageSelect}
          activeTeamParticipantVideos={activeTeamParticipantVideos}
          selectedTeam={selectedTeam}
          changeSelectedTeam={changeSelectedTeam}
          handleStartDrawing={handleStartDrawing}
          handleDraw={handleDraw}
          handleStopDrawing={handleStopDrawing}
          teamCanvasVideoRef={teamCanvasVideoRef}
          teamTextCanvasRef={teamTextCanvasRef}
          teamImageCanvasRef={teamImageCanvasRef}
          teamDrawCanvasRef={teamDrawCanvasRef}
          teamEraseCanvasRef={teamEraseCanvasRef}
          doErasing={doErasing}
          teamCompositeCanvasRef={teamCompositeCanvasRef}
          selectedTeamTextCanvas={selectedTeamTextCanvas}
          TeamTextCanvas={TeamTextCanvas}
          teamTextProps={teamTextProps}
          setTeamTextCanvas={setTeamTextCanvas}
          setTeamTextProps={setTeamTextProps}
          handleToolboxModeChange={handleToolboxModeChange}
          removeSelectedTeam={removeSelectedTeam}
          countdown={countdown}
          canvasVideoRef={canvasVideoRef}
          hiddenCanvasStyle={hiddenCanvasStyle}
          textCanvasRef={textCanvasRef}
          imageCanvasRef={imageCanvasRef}
          drawCanvasRef={drawCanvasRef}
          eraseCanvasRef={eraseCanvasRef}
          compositeCanvasRef={compositeCanvasRef}
        />
      ) : null}
    </React.Fragment>
  );
});

export default TopVideoWebCam;